import _ from 'lodash'

function formatDestionationsContent(destinations) {
  return _.reduce(destinations, (destinationMap, item) => ({
    ...destinationMap,
    [item.id]: item,
  }), {})
}

function constructDestinationsFromItinerary(itinerary, fallbackDestinations, destinationsContent) {
  if (_.isEmpty(itinerary)) {
    return _.map(fallbackDestinations, item => ({
      id: item,
      title: _.get(destinationsContent, `[${item}].name`)
    }))
  }
  return _.reduce(itinerary, (destinations, item) => {
    const updatedDestinations = _.cloneDeep(destinations)
    _.forEach(_.get(item, 'destinations', []), destinationItem => {
      if (_.get(destinationItem, 'destinationId') !== _.get(_.last(updatedDestinations), 'id')) {
        updatedDestinations.push({
          id: _.get(destinationItem, 'destinationId'),
          title: _.get(destinationsContent, `[${_.get(destinationItem, 'destinationId')}].name`)
        })
      }
    })
    return updatedDestinations
  }, [])
}

function formatTourDestinations(destinations) {
  const destinationList = _.map(destinations, destination => _.get(destination, 'title'));
  return _.filter(destinationList, destination => destination);
}

/**
 * An offer is an active flash sale if its isFlashSale flag is true,
 * it has a status value of true, and it meets the trip duration
 * requirements.
 */
function getActiveFlashSaleOffer(offers, numberOfNightsInTrip) {
  return _.find(offers, offer => {
    const isFlashSale = _.get(offer, 'flashSale')
    const offerStatus = _.get(offer, 'status')
    const requiredDuration = _.get(offer, 'requiredDuration')
      ? _.parseInt(_.get(offer, 'requiredDuration'))
      : 0
    return offerStatus && isFlashSale && numberOfNightsInTrip >= requiredDuration
  })
}

/**
 * We assume that if an offer description contains numeric digits, has a status
 * value of true, and meets the trip duration requirements, it is valid. Offers
 * are filtered based on that assumption, then all non-numeric characters are
 * stripped from the descriptions and the offers are sorted in descending order
 * by the discount amount.
 */
function getHighestDiscountOffer(offers, numberOfNightsInTrip) {
  const offersWithValidDiscounts = _.filter(offers,
    offer => {
      const requiredDuration = _.get(offer, 'requiredDuration')
        ? _.parseInt(_.get(offer, 'requiredDuration'))
        : 0
      return /\d/.test(_.get(offer, 'priceDescriptionPrimary'))
        && _.get(offer, 'status')
        && numberOfNightsInTrip >= requiredDuration
    }
  )
  const offersWithFormattedDiscounts = _.map(offersWithValidDiscounts, offer => {
    const offerDescription = _.get(offer, 'priceDescriptionPrimary')
    const discountValue = parseInt(offerDescription.replace(/\D/g,''), 10);
    return {
      ...offer,
      discountValue,
    }
  })
  const offersSortedByDiscountValue = _.orderBy(offersWithFormattedDiscounts, ['discountValue'], ['desc'])
  return offersSortedByDiscountValue[0]
}

/**
 * The priority offer is either the active flash sale offer
 * or the active offer with the highest discount.
 */
function getPriorityOffer(offers, tripDuration) {
  // The softrip duration is determined by the number of days in the trip, while the
  // offer duration is based on the number of nights. We subtract 1 from the duration
  // in order to determine whether the trip fits the offer duration.
  const numberOfNightsInTrip = tripDuration - 1
  const flashSaleOffer = getActiveFlashSaleOffer(offers, numberOfNightsInTrip)
  let priorityOffer = flashSaleOffer
  if (!flashSaleOffer) {
    priorityOffer = getHighestDiscountOffer(offers, numberOfNightsInTrip)
  }
  return _.get(priorityOffer, 'flag') || _.get(priorityOffer, 'priceDescriptionPrimary')
  }

export function formatTours(tours, destinations) {
  const destinationsContent = formatDestionationsContent(destinations)
  const viewableTours = _.filter(tours, tour => !!_.get(tour, 'viewExtensions.published'))

  return _.map(viewableTours, tour => ({
    id: _.get(tour, 'id'),
    advertisedPrice: _.get(tour, 'viewExtensions.advertisedPrice', '').toLocaleString(),
    destinations: formatTourDestinations(
      constructDestinationsFromItinerary(
        _.get(tour, 'viewExtensions.itinerary', []),
        _.get(tour, 'viewExtensions.locationIds', []),
        destinationsContent,
      )
    ),
    duration: _.get(tour, 'viewExtensions.duration'),
    image: _.get(tour, 'viewExtensions.images.image.path')
      || _.get(tour, 'viewExtensions.images.heroImage.path')
      || _.get(tour, 'viewExtensions.images.image')
      || _.get(tour, 'viewExtensions.images.detailsImage.path'),
    map: _.get(tour, 'viewExtensions.images.mapImage.path')
      || _.get(tour, 'viewExtensions.images.mapImage'),
    offer: getPriorityOffer(
      _.get(tour, 'viewExtensions.offers', []),
      _.get(tour, 'viewExtensions.duration'),
    ),
    title: _.get(tour, 'basicInfo.name'),
    tripTag:  _.get(tour, 'viewExtensions.tripTag.title') && _.get(tour, 'viewExtensions.tripTag'),
    url: _.get(tour, 'viewExtensions.url'),
  }))
}
